import { Distribution, Payout, RevenueSplit } from './revenue.domain';
import _ from 'lodash';
import { RevenueViewDispatcher } from './useRevenueModel';

type TableSchema<T> = Array<{
  key: keyof T;
  label: string;
  width: string | number;
  sx?: object;
}>;

const revenueSplitTableSchema: TableSchema<RevenueSplit> = [
  { key: 'productType', label: 'Product Type', width: '33%' },
  { key: 'referralPercent', label: 'Referral (%)', width: '33%' },
  { key: 'playbookPercent', label: 'Playbook Fee (%)', width: '33%' },
];

const revenueSplitTableSchemaWithEngamement: TableSchema<RevenueSplit> = [
  { key: 'productType', label: 'Product Type', width: '25%' },
  { key: 'referralPercent', label: 'Referral (%)', width: '25%' },
  { key: 'engagementPercent', label: 'Engagement (%)', width: '25%' },
  { key: 'playbookPercent', label: 'Playbook Fee (%)', width: '25%' },
];

const distributionTableSchema: TableSchema<Distribution> = [
  { key: 'productType', label: 'Product Type', width: '50%' },
  { key: 'distributionType', label: 'Schedule', width: '50%' },
];

const payoutTableSchema: TableSchema<Payout> = [
  { key: 'ruleType', label: 'Type', width: '30%' },
  { key: 'amount', label: 'Amount', width: '35%' },
  { key: 'startDate', label: 'Start', width: '35%' },
  { key: 'endDate', label: 'End', width: '35%' },
];

export type TableCell = {
  value: string | number;
  input?: {
    value: number | undefined;
    onChange: (value: number | undefined) => void;
    disabled: boolean;
  };
};

type RevenueCTA = Array<{
  label: string;
  onClick: () => void;
  variant: 'outlined' | 'contained' | 'text';
}>;

export type RevenueTableView<T> = {
  header: {
    cells: TableSchema<T>;
    sx?: object;
  };
  rows: Array<Array<TableCell>>;
  cta?: RevenueCTA;
  tableSx?: object;
};

const getRevenueCTA = (
  isEditMode: boolean,
  dispatch: RevenueViewDispatcher,
): RevenueCTA => {
  if (isEditMode) {
    return [
      {
        label: 'Cancel',
        onClick: () => dispatch({ type: 'cancelRevenueSplitEdit' }),
        variant: 'outlined',
      },
      {
        label: 'Save',
        onClick: () => dispatch({ type: 'saveRevenueSplit' }),
        variant: 'contained',
      },
    ];
  }
  return [
    {
      label: 'Edit',
      onClick: () => dispatch({ type: 'editRevenueSplit' }),
      variant: 'contained',
    },
  ];
};

const getRevenueInputProps = (p: {
  key: string;
  item: RevenueSplit;
  dispatch: RevenueViewDispatcher;
}) => {
  switch (p.key) {
    case 'referralPercent':
      return {
        value: p.item[p.key],
        onChange: (value: number | undefined) => null,
        disabled: true,
      };
    case 'playbookPercent':
      return {
        value: p.item[p.key],
        onChange: (value: number | undefined) =>
          p.dispatch({
            type: 'updateSplitRule',
            payload: {
              value: value,
              productType: p.item['productType'],
            },
          }),
        disabled: false,
      };
    default:
      return undefined;
  }
};

export type RevenueSplitView = RevenueTableView<RevenueSplit>;

export const toRevenueSplitView = (
  data: Array<RevenueSplit>,
  isEditMode: boolean,
  dispatch: RevenueViewDispatcher,
  hasPayoutPermission: boolean,
): RevenueSplitView => {
  const hasEngamement = data.some(
    (item) => item.engagementPercent && item.engagementPercent > 0,
  );
  const mappedTableSchema = hasEngamement
    ? revenueSplitTableSchemaWithEngamement
    : revenueSplitTableSchema;
  const rows = mapRawRevenueSplitToRevenueSplit(data).map((item) => {
    return mappedTableSchema.map(({ key }) => ({
      value: item[key],
      input: isEditMode
        ? getRevenueInputProps({ key, item, dispatch })
        : undefined,
    }));
  });
  const hasCustomRules = data.length > 1;

  return {
    rows,
    header: {
      cells: mappedTableSchema,
      sx: isEditMode
        ? {
            backgroundColor: 'white',
          }
        : {},
    },
    tableSx: isEditMode
      ? {
          boxShadow: 'none',
        }
      : {},
    cta: !hasPayoutPermission
      ? []
      : hasEngamement || hasCustomRules
      ? []
      : getRevenueCTA(isEditMode, dispatch),
  };
};

export type DistributionView = RevenueTableView<Distribution>;

export const toDistributionView = (
  data: Array<Distribution>,
): DistributionView => {
  const rows = data.map((item) => {
    return distributionTableSchema.map(({ key }) => ({
      value: item[key],
    }));
  });
  return {
    rows,
    header: {
      cells: distributionTableSchema,
    },
  };
};

const mapRawPayoutToViewData = (data: Array<Payout>) => {
  return data.map((item) => ({
    ...item,
    startDate: new Date(item.startDate).toLocaleDateString('en-US'),
    endDate: item.endDate
      ? new Date(item.endDate).toLocaleDateString('en-US')
      : '-',
  }));
};

export type PayoutView = RevenueTableView<Payout>;
export const toPayoutView = (data: Array<Payout>): PayoutView => {
  const rows = mapRawPayoutToViewData(data).map((item) => {
    return payoutTableSchema.map(({ key }) => ({
      value: item[key],
    }));
  });
  return {
    rows,
    header: {
      cells: payoutTableSchema,
    },
  };
};

export const mapRawRevenueSplitToRevenueSplit = (
  revenueSplit: Array<RevenueSplit>,
) => {
  if (!revenueSplit) return [];
  return revenueSplit.map((item) => ({
    ...item,
    productType: item.productType === 'none' ? 'All' : item.productType,
  }));
};
